import { Component } from '@theme/component';
import {
  afterNextPaint,
  debounce,
  isClickedOutside,
  onAnimationEnd,
  yieldToMainThread,
} from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {boolean} */
  #openScheduled = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   *
   * For `[data-fast-open]` (search): critical path only ensures the lightweight
   * transform shell is visible. `showModal()` and side-effects run after paint
   * so INP stays under 50ms.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#openScheduled) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    if (dialog.hasAttribute('data-fast-open')) {
      this.#openScheduled = true;
      // Shell may already be open from the capture pointerdown listener.
      document.getElementById('search-modal-shell')?.classList.add('is-open');

      afterNextPaint(async () => {
        this.#openScheduled = false;
        const shellOpen = document.getElementById('search-modal-shell')?.classList.contains('is-open');
        if (!shellOpen && !dialog.open) return;

        await yieldToMainThread();
        if (dialog.open) {
          this.#hideSearchShell();
          return;
        }

        this.#completeOpen(dialog, scrollY);
      });
      return;
    }

    dialog.showModal();
    afterNextPaint(() => {
      if (!dialog.open) return;
      this.#attachOpenListeners();
      this.#applyBodyScrollLock(scrollY);
      this.dispatchEvent(new DialogOpenEvent());
    });
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {number} scrollY
   */
  #completeOpen(dialog, scrollY) {
    if (!dialog.open) {
      dialog.showModal();
    }

    this.#hideSearchShell();
    this.#attachOpenListeners();
    this.#applyBodyScrollLock(scrollY);
    this.dispatchEvent(new DialogOpenEvent());
  }

  #hideSearchShell() {
    document.getElementById('search-modal-shell')?.classList.remove('is-open');
  }

  #attachOpenListeners() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * @param {number} scrollY
   */
  #applyBodyScrollLock(scrollY) {
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    this.#openScheduled = false;
    this.#hideSearchShell();

    if (!dialog.open) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      this.dispatchEvent(new DialogCloseEvent());
      return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    const { dialog } = this.refs;
    const shellOpen = document.getElementById('search-modal-shell')?.classList.contains('is-open');
    if (dialog.open || shellOpen || this.#openScheduled) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

/** @type {number} */
let scrollLockOpenFrame = 0;

/**
 * Apply document scroll-lock after the next paints so open UI (drawers/modals)
 * can present before `html[scroll-lock] { overflow: hidden }` forces a
 * full-document style recalculation.
 * @param {boolean} lock
 */
function setDocumentScrollLock(lock) {
  if (scrollLockOpenFrame) {
    cancelAnimationFrame(scrollLockOpenFrame);
    scrollLockOpenFrame = 0;
  }

  if (!lock) {
    document.documentElement.removeAttribute('scroll-lock');
    return;
  }

  scrollLockOpenFrame = requestAnimationFrame(() => {
    scrollLockOpenFrame = requestAnimationFrame(() => {
      scrollLockOpenFrame = 0;
      document.documentElement.setAttribute('scroll-lock', '');
    });
  });
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        setDocumentScrollLock(event.target.open);
      }
    }
  },
  { capture: true }
);
